<template>
  <div class="contract-variables">
    <!-- Contador no topo -->
    <div
      class="variables-meta"
      v-if="!loading && contract_variables.length > 0"
    >
      <span class="meta-count">
        <i class="fas fa-layer-group"></i>
        {{ contract_variables.length }} variáveis disponíveis
      </span>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="state-loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Carregando variáveis...</span>
    </div>

    <!-- Empty State -->
    <div v-show="contract_variables.length == 0" class="state-empty">
      <i class="fas fa-code"></i>
      <span>Nenhuma variável encontrada</span>
    </div>

    <!-- Lista de variáveis -->
    <div v-show="contract_variables.length > 0" class="variables-list">
      <button
        v-for="(item, index) in contract_variables"
        :key="index"
        class="variable-item"
        :class="{ 'variable-item--copied': copiedIndex === index }"
        @click="handleClick(item.variable, index)"
        :aria-label="`Inserir variável: ${item.description}`"
        type="button"
      >
        <!-- Lado esquerdo: descrição em destaque -->
        <div class="variable-body">
          <span class="variable-desc">{{ item.description }}</span>
          <code class="variable-code">{{ formatBrace(item.variable) }}</code>
        </div>

        <!-- Ícone de ação direita -->
        <div class="variable-action">
          <i
            :class="copiedIndex === index ? 'fas fa-check' : 'fas fa-copy'"
            class="action-icon"
          ></i>
        </div>
      </button>
    </div>

    <!-- Footer -->
    <div class="variables-footer">
      <i class="fas fa-mouse-pointer"></i>
      <span>Clique para inserir no texto do contrato</span>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

export default {
  name: "ContractVariableList",

  data() {
    return {
      contract_variables: [],
      loading: false,
      // Controla qual item mostrou feedback de "copiado"
      copiedIndex: null,
      copiedTimer: null,
    };
  },

  async mounted() {
    await this.getContractVariables();
  },

  beforeDestroy() {
    // Limpa timer ao destruir o componente
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  },

  methods: {
    async getContractVariables() {
      try {
        this.loading = true;
        const response = await api.get("/contract/contract-variables");
        const { contract_variables } = response.data;
        this.contract_variables = contract_variables || [];
      } catch (error) {
        console.error(error);
        toast("Erro ao carregar variáveis do contrato", "error");
      } finally {
        this.loading = false;
      }
    },

    formatBrace(valor) {
      return `{${valor}}`;
    },

    /**
     * Emite o evento de inserção para o componente pai
     * (ContractStrandard / ContractStrandardNew)
     * E também copia para o clipboard como fallback.
     */
    async handleClick(variable, index) {
      const formatted = this.formatBrace(variable);

      // // 1. Emite para o pai inserir no textarea
      // this.$emit("insert", formatted);

      // 2. Feedback visual temporário no item clicado
      this.copiedIndex = index;
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copiedIndex = null;
      }, 1500);

      // 3. Copia para clipboard também (comportamento anterior mantido)
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(formatted);
        } else {
          const el = document.createElement("textarea");
          el.value = formatted;
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        }
      } catch (e) {
        console.error("Erro ao copiar:", e);
      }
    },
  },
};
</script>

<style scoped>
/* ===========================================
   CONTAINER PRINCIPAL
   Sem card próprio — o card é do pai.
   O componente só gerencia a lista.
   =========================================== */

.contract-variables {
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* ===========================================
   META: contador de variáveis
   =========================================== */

.variables-meta {
  padding: 0 0 10px 0;
}

.meta-count {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.meta-count i {
  font-size: 11px;
  color: #cbd5e1;
}

/* ===========================================
   ESTADOS: loading e vazio
   =========================================== */

.state-loading,
.state-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: #94a3b8;
  font-size: 13px;
  font-family: "WorkSans-Regular", sans-serif;
}

.state-loading i,
.state-empty i {
  font-size: 24px;
  color: #cbd5e1;
}

/* ===========================================
   LISTA DE VARIÁVEIS
   =========================================== */

.variables-list {
  display: flex;
  flex-direction: column;
  gap: 0;

  /* Scroll com fade nas bordas para indicar
     que há mais conteúdo */
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden; /* o scroll fica no wrapper abaixo */
}

/* Wrapper com scroll e sombra de continuidade */
.variables-list {
  overflow-y: auto;
}

/* Scrollbar discreta */
.variables-list::-webkit-scrollbar {
  width: 4px;
}
.variables-list::-webkit-scrollbar-track {
  background: #f8fafc;
}
.variables-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.variables-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* ===========================================
   ITEM DA VARIÁVEL
   Hierarquia: descrição legível > código técnico
   =========================================== */

.variable-item {
  /* Reset de botão */
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background: white;
  cursor: pointer;
  width: 100%;
  text-align: left;

  /* Layout */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;

  /* Transição suave */
  transition: background 0.15s ease;
}

.variable-item:last-child {
  border-bottom: none;
}

.variable-item:hover {
  background: #f0f7ff;
}

/* Estado: acabou de ser copiado/inserido */
.variable-item--copied {
  background: #f0fdf4;
}

.variable-item--copied .action-icon {
  color: #16a34a !important;
}

.variable-item--copied .variable-code {
  color: #16a34a;
  background: #dcfce7;
}

/* ===========================================
   CORPO DO ITEM
   =========================================== */

.variable-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 0; /* evita overflow em textos longos */
}

/* Descrição: texto principal, leitura em destaque */
.variable-desc {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;

  /* Trunca textos muito longos */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Código técnico: badge secundário, menor */
.variable-code {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #0f65b0;
  background: #dbeafe;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.2px;
  line-height: 1.6;

  /* Nunca cresce além do container */
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: background 0.15s ease, color 0.15s ease;
}

/* ===========================================
   ÍCONE DE AÇÃO
   =========================================== */

.variable-action {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f1f5f9;
  transition: background 0.15s ease;
}

.variable-item:hover .variable-action {
  background: #dbeafe;
}

.action-icon {
  font-size: 11px;
  color: #94a3b8;
  transition: color 0.15s ease;
}

.variable-item:hover .action-icon {
  color: #0f65b0;
}

/* ===========================================
   FOOTER
   =========================================== */

.variables-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 2px 0 2px;
  font-size: 11px;
  color: #94a3b8;
  font-family: "WorkSans-Regular", sans-serif;
}

.variables-footer i {
  font-size: 11px;
  color: #f59e0b;
}

/* ===========================================
   RESPONSIVO
   =========================================== */

@media (max-width: 768px) {
  .variable-item {
    padding: 14px 12px; /* área de toque maior no mobile */
  }

  .variable-desc {
    font-size: 14px; /* levemente maior para leitura em tela pequena */
  }

  .variables-list {
    max-height: 280px;
  }
}
</style>